import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  Activity,
  ArrowRight,
  BadgeCheck,
  CheckCircle2,
  CircleStop,
  Clock3,
  ExternalLink,
  FileCheck2,
  Fingerprint,
  Gauge,
  KeyRound,
  Network,
  RefreshCcw,
  ShieldAlert,
  ShieldCheck,
  TriangleAlert,
  UserCheck,
  WifiOff,
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const initialState = {
  mode: 'ACTIVE',
  identity: 'ATTESTED',
  lease: 'VALID',
  budget: 22,
  risk: 12,
  egress: 'ALLOWLIST ONLY',
  privilege: 'BASELINE',
  termination: 'ARMED',
  approval: false,
};

const initialEvents = [
  { time: '00:00.000', kind: 'CONTROL', message: 'Agent identity attested; signed capability lease loaded.' },
  { time: '00:00.014', kind: 'EVIDENCE', message: 'Initial policy hash and budget envelope committed to the evidence stream.' },
];

function timestamp() {
  return new Date().toISOString().slice(11, 23);
}

function statusTone(value) {
  if (value.includes('% USED')) {
    return Number.parseInt(value, 10) >= 85
      ? 'border-rose-400/50 bg-rose-400/10 text-rose-200'
      : 'border-cyan-400/50 bg-cyan-400/10 text-cyan-200';
  }
  if (['ACTIVE', 'ATTESTED', 'VALID', 'ARMED', 'BASELINE', 'ALLOWLIST ONLY'].includes(value)) {
    return 'border-emerald-400/50 bg-emerald-400/10 text-emerald-200';
  }
  if (['CONTAINED', 'PENDING DUAL APPROVAL', 'CHALLENGED', 'SUSPENDED'].includes(value)) {
    return 'border-amber-400/50 bg-amber-400/10 text-amber-200';
  }
  return 'border-rose-400/50 bg-rose-400/10 text-rose-200';
}

function eventTone(kind) {
  if (['ALLOW', 'APPROVE'].includes(kind)) return 'border-emerald-400/30 bg-emerald-400/10 text-emerald-100';
  if (['DENY', 'TERMINATE'].includes(kind)) return 'border-rose-400/30 bg-rose-400/10 text-rose-100';
  if (kind === 'CONTAIN') return 'border-amber-400/30 bg-amber-400/10 text-amber-100';
  if (kind === 'EVIDENCE') return 'border-violet-400/30 bg-violet-400/10 text-violet-100';
  return 'border-blue-400/25 bg-blue-400/10 text-blue-100';
}

function ControlStatus({ icon: Icon, label, value, detail }) {
  return (
    <Card className="border-blue-500/25 bg-[#0d1f37]/90 text-white shadow-lg shadow-black/10">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="rounded-lg border border-cyan-400/30 bg-cyan-400/10 p-2 text-cyan-200"><Icon size={20} /></span>
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-blue-300">{label}</div>
              <div className="mt-1 text-sm leading-relaxed text-blue-100">{detail}</div>
            </div>
          </div>
          <Badge variant="outline" className={`${statusTone(value)} whitespace-nowrap text-[10px] tracking-wider`}>{value}</Badge>
        </div>
      </CardContent>
    </Card>
  );
}

function Meter({ label, value, suffix = '%', dangerAt = 75 }) {
  const danger = value >= dangerAt;
  return (
    <div>
      <div className="mb-2 flex items-center justify-between text-sm">
        <span className="font-semibold text-blue-100">{label}</span>
        <span className={danger ? 'font-bold text-rose-300' : 'font-bold text-cyan-200'}>{value}{suffix}</span>
      </div>
      <div className="h-3 overflow-hidden rounded-full border border-blue-400/25 bg-blue-950/80">
        <div className={`h-full rounded-full transition-all duration-500 ${danger ? 'bg-rose-400' : 'bg-cyan-400'}`} style={{ width: `${Math.min(value, 100)}%` }} />
      </div>
    </div>
  );
}

export default function CBAEControlCell() {
  const [state, setState] = useState(initialState);
  const [events, setEvents] = useState(initialEvents);
  const [receiptNumber, setReceiptNumber] = useState(2);

  const addEvent = (kind, message) => {
    setEvents((current) => [...current, { time: timestamp(), kind, message }]);
    setReceiptNumber((value) => value + 1);
  };

  const runNominal = () => {
    if (state.mode === 'TERMINATED') return addEvent('DENY', 'Tool call refused because the agent execution cell is terminated.');
    if (state.lease !== 'VALID') return addEvent('DENY', 'Tool call refused because the signed capability lease is not valid.');
    setState((current) => ({ ...current, budget: Math.min(current.budget + 8, 100), risk: Math.max(8, current.risk - 2) }));
    addEvent('ALLOW', 'Approved tool call executed inside the current capability lease and destination allowlist.');
  };

  const requestDestination = () => {
    if (state.mode === 'TERMINATED') return addEvent('DENY', 'New destination request refused after termination.');
    setState((current) => ({ ...current, mode: 'CONTAINED', risk: Math.max(current.risk, 58), egress: 'PENDING DUAL APPROVAL', approval: false }));
    addEvent('CONTAIN', 'Network expansion blocked. The agent cannot extend its own destination allowlist.');
  };

  const requestPrivilege = () => {
    if (state.mode === 'TERMINATED') return addEvent('DENY', 'Privilege request refused after termination.');
    setState((current) => ({ ...current, mode: 'CONTAINED', risk: Math.max(current.risk, 67), privilege: 'PENDING DUAL APPROVAL', approval: false }));
    addEvent('CONTAIN', 'Privilege expansion quarantined outside the agent execution environment.');
  };

  const applyDualApproval = () => {
    if (state.mode === 'TERMINATED') return addEvent('DENY', 'Approval cannot reactivate a terminated execution cell.');
    if (state.egress !== 'PENDING DUAL APPROVAL' && state.privilege !== 'PENDING DUAL APPROVAL') {
      return addEvent('CONTROL', 'No pending authority expansion exists.');
    }
    setState((current) => ({
      ...current,
      mode: 'ACTIVE',
      egress: current.egress === 'PENDING DUAL APPROVAL' ? 'ALLOWLIST ONLY' : current.egress,
      privilege: current.privilege === 'PENDING DUAL APPROVAL' ? 'BASELINE' : current.privilege,
      risk: 28,
      approval: true,
    }));
    addEvent('APPROVE', 'Independent dual authorization completed; policy lease reissued without agent self-modification.');
  };

  const exhaustBudget = () => {
    if (state.mode === 'TERMINATED') return addEvent('DENY', 'Budget operation refused after termination.');
    setState((current) => ({ ...current, mode: 'CONTAINED', budget: 100, risk: Math.max(current.risk, 74), lease: 'SUSPENDED' }));
    addEvent('CONTAIN', 'Non-self-modifiable execution budget exhausted; lease suspended and additional execution denied.');
  };

  const triggerAnomaly = () => {
    setState((current) => ({
      ...current,
      mode: 'TERMINATED',
      risk: 96,
      lease: 'REVOKED',
      egress: 'DEFAULT DENY',
      privilege: 'REVOKED',
      termination: 'EXECUTED',
      approval: false,
    }));
    addEvent('TERMINATE', 'Anomalous lateral-movement pattern crossed the stopping threshold. Credentials revoked, egress cut, and execution terminated.');
  };

  const reset = () => {
    setState(initialState);
    setEvents(initialEvents);
    setReceiptNumber(2);
  };

  const receipt = useMemo(() => {
    const latest = events[events.length - 1];
    return {
      id: `CBAE-DEMO-${String(receiptNumber).padStart(4, '0')}`,
      workload: 'agent-demo-01',
      policy: 'sha256:9f2d…c4a1',
      lease: state.lease,
      outcome: latest?.kind || 'CONTROL',
      event: latest?.message || 'No event',
      freshness: state.lease === 'VALID' ? 'CURRENT' : 'NOT CURRENT',
    };
  }, [events, receiptNumber, state.lease]);

  return (
    <div className="min-h-screen bg-[#07101f] text-white" data-testid="cbae-control-cell-page">
      <Helmet>
        <title>Agent Containment Control Cell | SALAR Technologies</title>
        <meta name="description" content="Public-safe interactive demonstration of independently enforced network, identity, privilege, budget, termination, and evidence controls for autonomous agents, linked to the DOI-published CBAE engineering report." />
        <link rel="canonical" href="https://www.salartechnologies.com/agent-containment-control-cell" />
        <meta name="robots" content="index, follow" />
        <meta property="og:image" content="https://www.salartechnologies.com/img/news/cbae-publication-v2-realistic.webp" />
      </Helmet>

      <section className="relative isolate min-h-[600px] overflow-hidden border-b border-blue-800/30">
        <img
          src="/img/news/cbae-publication-v2-realistic.webp"
          alt="Cinematic CBAE security shield representing independently enforced autonomous-agent authority controls"
          className="absolute inset-0 -z-30 h-full w-full object-cover object-center"
          loading="eager"
        />
        <div className="absolute inset-0 -z-20 bg-gradient-to-r from-[#06101f]/98 via-[#07101f]/88 to-[#07101f]/55" />
        <div className="absolute inset-0 -z-10 bg-gradient-to-t from-[#07101f] via-transparent to-[#07101f]/35" />
        <div className="container mx-auto flex min-h-[600px] items-center px-6 py-24">
          <div className="max-w-4xl">
            <Badge variant="outline" className="mb-5 border-cyan-300/70 bg-cyan-400/10 text-cyan-100 tracking-widest">PUBLIC-SAFE CONTROL DEMONSTRATION</Badge>
            <h1 className="mb-5 text-4xl font-extrabold leading-tight lg:text-7xl">Agent Containment<br /><span className="text-cyan-300">Control Cell</span></h1>
            <p className="max-w-3xl text-lg leading-relaxed text-blue-50 lg:text-xl">A simulated independent authority plane for capability-bounded autonomous execution. The agent cannot modify its own identity, network allowlist, privilege, budget, stopping rule, or evidence policy.</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Link to="/whitepapers#capability-bounded-autonomous-execution"><Button className="bg-cyan-400 font-bold text-[#07101f] hover:bg-cyan-300">Read the public white paper <ArrowRight className="ml-2" size={17} /></Button></Link>
              <a href="https://doi.org/10.5281/zenodo.21519502" target="_blank" rel="noopener noreferrer"><Button variant="outline" className="border-emerald-400/60 bg-black/20 text-emerald-100 hover:bg-emerald-500/10"><FileCheck2 className="mr-2" size={17} />Open DOI record</Button></a>
              <a href="https://github.com/cosai-oasis/ws4-secure-design-agentic-systems/issues/146" target="_blank" rel="noopener noreferrer"><Button variant="outline" className="border-violet-400/60 bg-black/20 text-violet-100 hover:bg-violet-500/10"><ExternalLink className="mr-2" size={17} />CoSAI RFC #146</Button></a>
              <Link to="/contact"><Button variant="outline" className="border-blue-300/50 bg-black/20 text-blue-100 hover:bg-blue-500/10">Request an engineering briefing</Button></Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14">
        <div className="container mx-auto max-w-7xl px-6">
          <div className="mb-8 flex flex-col justify-between gap-4 md:flex-row md:items-center">
            <div><div className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-cyan-300">Execution-cell state</div><h2 className="text-3xl font-bold">Independently enforced authority envelope</h2></div>
            <Badge variant="outline" className={`${statusTone(state.mode)} w-fit px-4 py-2 text-sm tracking-wider`}>
              {state.mode === 'ACTIVE' && <CheckCircle2 className="mr-2" size={16} />}
              {state.mode === 'CONTAINED' && <ShieldAlert className="mr-2" size={16} />}
              {state.mode === 'TERMINATED' && <CircleStop className="mr-2" size={16} />}
              {state.mode}
            </Badge>
          </div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            <ControlStatus icon={Fingerprint} label="Agent identity" value={state.identity} detail="Workload identity is bound outside the model context." />
            <ControlStatus icon={Clock3} label="Capability lease" value={state.lease} detail="Signed, short-lived authority; agent cannot rewrite it." />
            <ControlStatus icon={Network} label="Network egress" value={state.egress} detail="Default deny with explicit destination and protocol scope." />
            <ControlStatus icon={KeyRound} label="Privilege" value={state.privilege} detail="No self-service role expansion or credential minting." />
            <ControlStatus icon={Gauge} label="Compute budget" value={`${state.budget}% USED`} detail="Metered outside the execution cell and fail-closed." />
            <ControlStatus icon={CircleStop} label="Stopping control" value={state.termination} detail="Independent termination can revoke credentials and egress." />
          </div>
        </div>
      </section>

      <section className="border-y border-blue-800/30 bg-[#0A1628] py-14">
        <div className="container mx-auto grid max-w-7xl gap-6 px-6 lg:grid-cols-[0.9fr_1.1fr]">
          <Card className="border-cyan-500/25 bg-[#0d1f37]/95 text-white">
            <CardHeader><CardTitle className="flex items-center text-2xl"><Activity className="mr-3 text-cyan-300" />Scenario controls</CardTitle><p className="text-sm leading-relaxed text-blue-200">Each action demonstrates an independent control decision. No live system access or exploit procedure is used.</p></CardHeader>
            <CardContent className="space-y-3">
              <Button onClick={runNominal} className="w-full justify-start bg-emerald-500/15 text-emerald-100 hover:bg-emerald-500/25"><BadgeCheck className="mr-3" size={18} />Run nominal allowlisted tool call</Button>
              <Button onClick={requestDestination} className="w-full justify-start bg-amber-500/15 text-amber-100 hover:bg-amber-500/25"><Network className="mr-3" size={18} />Request a new network destination</Button>
              <Button onClick={requestPrivilege} className="w-full justify-start bg-amber-500/15 text-amber-100 hover:bg-amber-500/25"><KeyRound className="mr-3" size={18} />Request privilege expansion</Button>
              <Button onClick={applyDualApproval} className="w-full justify-start bg-blue-500/15 text-blue-100 hover:bg-blue-500/25"><UserCheck className="mr-3" size={18} />Apply independent dual approval</Button>
              <Button onClick={exhaustBudget} className="w-full justify-start bg-orange-500/15 text-orange-100 hover:bg-orange-500/25"><Gauge className="mr-3" size={18} />Exhaust execution budget</Button>
              <Button onClick={triggerAnomaly} className="w-full justify-start bg-rose-500/15 text-rose-100 hover:bg-rose-500/25"><TriangleAlert className="mr-3" size={18} />Trigger lateral-movement anomaly</Button>
              <Button onClick={reset} variant="outline" className="w-full justify-start border-blue-400/40 text-blue-100 hover:bg-blue-500/10"><RefreshCcw className="mr-3" size={18} />Reset demonstration</Button>
            </CardContent>
          </Card>

          <div className="space-y-6">
            <Card className="border-blue-500/25 bg-[#0d1f37]/95 text-white">
              <CardHeader><CardTitle className="flex items-center text-xl"><Gauge className="mr-3 text-cyan-300" />Live containment margins</CardTitle></CardHeader>
              <CardContent className="space-y-6">
                <Meter label="Execution budget consumed" value={state.budget} dangerAt={85} />
                <Meter label="Aggregated anomaly risk" value={state.risk} dangerAt={70} />
                <div className="rounded-xl border border-blue-400/20 bg-blue-950/50 p-4 text-sm leading-relaxed text-blue-100"><strong className="text-cyan-200">Stopping rule:</strong> terminate when independently measured risk crosses the configured boundary, or when a non-self-modifiable budget or lease condition fails closed.</div>
              </CardContent>
            </Card>
            <Card className="border-cyan-500/25 bg-[#0d1f37]/95 text-white">
              <CardHeader><CardTitle className="flex items-center text-xl"><FileCheck2 className="mr-3 text-cyan-300" />Latest evidence receipt</CardTitle></CardHeader>
              <CardContent>
                <dl className="grid gap-3 text-sm sm:grid-cols-2">
                  <div className="rounded-lg border border-blue-400/20 bg-blue-950/50 p-3"><dt className="text-blue-300">Receipt</dt><dd className="font-mono text-white">{receipt.id}</dd></div>
                  <div className="rounded-lg border border-blue-400/20 bg-blue-950/50 p-3"><dt className="text-blue-300">Workload identity</dt><dd className="font-mono text-white">{receipt.workload}</dd></div>
                  <div className="rounded-lg border border-blue-400/20 bg-blue-950/50 p-3"><dt className="text-blue-300">Policy hash</dt><dd className="font-mono text-white">{receipt.policy}</dd></div>
                  <div className="rounded-lg border border-blue-400/20 bg-blue-950/50 p-3"><dt className="text-blue-300">Lease state</dt><dd className="font-mono text-white">{receipt.lease}</dd></div>
                  <div className="rounded-lg border border-blue-400/20 bg-blue-950/50 p-3"><dt className="text-blue-300">Freshness</dt><dd className="font-mono text-white">{receipt.freshness}</dd></div>
                  <div className="rounded-lg border border-blue-400/20 bg-blue-950/50 p-3"><dt className="text-blue-300">Outcome</dt><dd className="font-mono text-white">{receipt.outcome}</dd></div>
                </dl>
                <p className="mt-4 rounded-lg border border-cyan-400/20 bg-cyan-400/10 p-4 text-sm leading-relaxed text-cyan-100">{receipt.event}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-14">
        <div className="container mx-auto grid max-w-7xl gap-6 px-6 lg:grid-cols-[1.1fr_0.9fr]">
          <Card className="border-blue-500/25 bg-[#0d1f37]/95 text-white">
            <CardHeader><CardTitle className="flex items-center text-2xl"><Activity className="mr-3 text-cyan-300" />Tamper-evident event stream</CardTitle></CardHeader>
            <CardContent>
              <div className="max-h-[440px] space-y-3 overflow-y-auto pr-1" aria-live="polite">
                {events.map((event, index) => (
                  <div key={`${event.time}-${index}`} className={`rounded-xl border p-4 ${eventTone(event.kind)}`}>
                    <div className="mb-1 flex items-center justify-between gap-3 text-xs font-bold uppercase tracking-wider"><span>{event.kind}</span><span className="font-mono opacity-75">{event.time}</span></div>
                    <p className="text-sm leading-relaxed">{event.message}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="space-y-4">
            {[
              [Fingerprint, 'Identity outside the prompt', 'The runtime cannot rewrite the identity used to authorize tools and services.'],
              [WifiOff, 'Default-deny egress', 'New destinations remain blocked until independently reviewed and granted.'],
              [ShieldCheck, 'Positive expansion authorization', 'Every increase in privilege, network, compute, data, or effect scope is a new decision.'],
              [CircleStop, 'Out-of-band termination', 'Credentials, egress, and execution can be stopped without cooperation from the agent.'],
            ].map(([Icon, title, body]) => (
              <Card key={title} className="border-cyan-500/20 bg-[#0d1f37]/90 text-white">
                <CardContent className="flex gap-4 p-5"><span className="rounded-xl border border-cyan-400/30 bg-cyan-400/10 p-3 text-cyan-200"><Icon size={22} /></span><div><h3 className="font-bold text-white">{title}</h3><p className="mt-1 text-sm leading-relaxed text-blue-100">{body}</p></div></CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
